import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import AwsS3 from '@uppy/aws-s3';
import Uppy, { UppyFile } from '@uppy/core';
import useUppyState from '@uppy/react/lib/useUppyState';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { SSE } from 'sse.js';
import { v4 as uuidv4 } from 'uuid';

import { allowedFileTypes } from '../../../../../backend/src/matter/doc/allowed-extensions';
import { OrgPermission } from '../../../../../backend/src/organisation/organisation-permission';
import { UploadFailureReason } from '../../../../../backend/src/types/types';
import { Icon } from '../icon';
import { ActiveUploadsProgress } from './active-upload-progress';
import { FileDuplicatesInfo } from './duplicates-info';
import { FileExplorer } from './explorer';
import { FileUploadInput } from './uploader';
import { GenericUppyFile, getUppyAWSS3Options, UploadType } from './uppy';
import { ViewToggle } from './view-toggle';
import { View } from './view-toggle';
import { Button } from '../shadcn/button';
import { ButtonGroup } from '../shadcn/button-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../shadcn/dialog';
import { useEmailAttachmentWarnings } from '@/api/hooks/useEmailAttachmentWarnings';
import { APIBaseChronos } from '@/api/hosts';
import { useSendPendingFailures, PendingFailure } from '@/api/mutations/useSendPendingFailures';
import useGetMatterFiles from '@/api/queries/useGetMatterFiles';
import { useUserContext } from '@/Contexts/User';
import { ActiveJob } from '@/hooks/useJobProgress';

export function FileUploader({
  setFilesProcessing,
  setFilesExist,
  activeJobs = [],
  allDocumentsCaseId,
  isMatterCreator = false,
}: {
  setFilesProcessing?: (processing: boolean) => void;
  setFilesExist?: (v: boolean) => void;
  activeJobs?: ActiveJob[];
  allDocumentsCaseId?: string | null;
  isMatterCreator?: boolean;
}) {
  const { matterId } = useParams();
  if (!matterId) return null;

  const authInfo = useAuthInfo();
  const { user } = useUserContext();

  const [checkingForDuplicates, setCheckingForDuplicates] = useState(false);
  const [duplicateScanSuccess, setDuplicateScanSuccess] = useState(false);
  const [duplicateNumber, setDuplicateNumber] = useState(0);
  const [zipIsProcessing, setZipIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [completedUploadsCount, setCompletedUploadsCount] = useState(0);
  const [fileTypesModalOpen, setFileTypesModalOpen] = useState(false);

  const fileIdNameMap = useMemo(() => new Map<string, string>(), []);
  const completedUploads = useMemo(() => new Set<string>(), []);
  const completedUploadsInBatch: UppyFile<{ id?: string }, {}>[] = useMemo(() => [], []);
  const recordUploadedFileToDB = useMemo(
    () => new Worker(new URL('../../../workers/record-matter-doc-upload.ts', import.meta.url), { type: 'module' }),
    [],
  );

  const uploadIdRef = useRef<string | null>(null);
  const pendingFailuresRef = useRef<Array<PendingFailure>>([]);
  const uploadContainsZipRef = useRef<boolean>(false);
  // Track if we had an active zip job that just completed
  const previousActiveJobsRef = useRef<ActiveJob[]>([]);

  const { mutateAsync: sendPendingFailures } = useSendPendingFailures();
  const {
    data: files,
    refetch: refetchFiles,
    isLoading: isLoadingFiles,
  } = useGetMatterFiles(allDocumentsCaseId ?? null, matterId ?? null);

  const [view, setView] = useState<View>((files?.length ?? 0 > 0) ? View.Explorer : View.Upload);

  // Email attachment warnings - show toast notifications for unsupported email attachments
  useEmailAttachmentWarnings({
    caseId: allDocumentsCaseId ?? '',
    enabled: !!allDocumentsCaseId,
  });

  async function getAccessToken() {
    return await authInfo.tokens.getAccessToken();
  }

  const onApideckSuccess = useCallback(() => {
    refetchFiles();
    setView(View.Explorer);
  }, [refetchFiles]);

  const [uppy] = useState(() =>
    new Uppy({
      restrictions: {
        maxNumberOfFiles: 10000,
        allowedFileTypes,
      },

      allowMultipleUploadBatches: true,

      onBeforeFileAdded: (file) => {
        if (file.name === undefined) {
          return;
        }

        const id = uuidv4();
        const extension = file.name.split('.').length > 1 ? file.name.split('.').pop() : undefined;
        const extensionLowerCase = extension?.toLowerCase();
        const name = `${id}${extensionLowerCase ? `.${extensionLowerCase}` : ''}`;

        fileIdNameMap.set(id, file.name);

        // Make a new File with the correct name as S3 only cares about the File contents' name
        const newFile = new File([file.data], name, { type: file.data.type });

        return { ...file, data: newFile, name, meta: { ...file.meta, name } };
      },
    }).use(AwsS3, getUppyAWSS3Options(getAccessToken, matterId, UploadType.Raw)),
  );

  // SSE Listener for duplicate scan
  async function setupSSEListener(): Promise<void> {
    const accessToken = await getAccessToken();
    const sseResponse = new SSE(
      `${APIBaseChronos}/api/files/${allDocumentsCaseId}/duplicate-scan/${uploadIdRef.current}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    const transitionToExplorer = () => {
      setView(View.Explorer);
      setCheckingForDuplicates(false);
      setDuplicateScanSuccess(false);
      uploadIdRef.current = null;
      sseResponse.close();
    };

    sseResponse.onmessage = async (msg: { data: string }) => {
      const parsed: { duplicateNumber: number } = JSON.parse(msg.data);

      refetchFiles();
      setDuplicateNumber(parsed.duplicateNumber);
      setDuplicateScanSuccess(true);

      setTimeout(() => {
        transitionToExplorer();
      }, 2000);
    };

    sseResponse.onerror = () => {
      refetchFiles();
      setDuplicateScanSuccess(true);

      setTimeout(() => {
        transitionToExplorer();
      }, 2000);
    };
  }

  // Send upload failures to be recorded using the case-based endpoint with All Documents case
  const sendPendingFailuresCallback = useCallback(async () => {
    if (pendingFailuresRef.current.length > 0 && uploadIdRef.current && allDocumentsCaseId) {
      await sendPendingFailures({
        caseId: allDocumentsCaseId,
        uploadId: uploadIdRef.current,
        failures: pendingFailuresRef.current,
      });

      // Clear pending failures after sending
      pendingFailuresRef.current = [];
    }
  }, [allDocumentsCaseId, sendPendingFailures]);

  const requestWorkerUpdateFileDatabase = useCallback(
    async (isFinal = false) => {
      if (window.Worker && allDocumentsCaseId !== null) {
        const token = await getAccessToken();
        recordUploadedFileToDB.postMessage({
          accessToken: token,
          caseId: allDocumentsCaseId,
          files: completedUploadsInBatch.map((file) => {
            const id = file.name?.split('.')[0] ?? '';

            return {
              id,
              name: fileIdNameMap.get(id),
              size: file.size,
              extension: file.extension,
            };
          }),
          isFinal,
          uploadId: uploadIdRef.current,
        });

        completedUploadsInBatch.splice(0, completedUploadsInBatch.length);
      }
    },
    [allDocumentsCaseId, fileIdNameMap, completedUploadsInBatch, recordUploadedFileToDB],
  );

  const handleUppyUploadSuccess = useCallback(
    (file: UppyFile<{ id?: string }, {}> | undefined) => {
      if (file && !completedUploads.has(file.id)) {
        completedUploads.add(file.id);
        completedUploadsInBatch.push(file);

        // Track if any uploaded file is a zip
        if (file.extension === 'zip') {
          uploadContainsZipRef.current = true;
        }

        setCompletedUploadsCount(completedUploads.size);

        // Send every 10 completed docs to the file creation endpoint on the backend
        // which uses a web worker to keep the main thread focused on uploading
        if (completedUploadsInBatch.length > 10) {
          requestWorkerUpdateFileDatabase();
        }
      }
    },
    [completedUploads, completedUploadsInBatch, requestWorkerUpdateFileDatabase],
  );

  const handleUppyUploadComplete = useCallback(async () => {
    await requestWorkerUpdateFileDatabase(true);
    // Wait for worker to complete
    await new Promise((resolve) => (recordUploadedFileToDB.onmessage = resolve));

    await sendPendingFailuresCallback();

    uppy.resetProgress();
    uppy.removeFiles([...completedUploads].map((id) => id));

    setCompletedUploadsCount(0);
    completedUploads.clear();
    fileIdNameMap.clear();

    setCompletedUploadsCount(0);
    setIsUploading(false);

    // Only start duplicate scan for non-zip uploads
    // For zip uploads, the useEffect monitoring activeJobs will handle it after extraction
    if (!uploadContainsZipRef.current) {
      setCheckingForDuplicates(true);
      setupSSEListener();
    }
    // Reset the flag for next upload
    uploadContainsZipRef.current = false;
  }, [
    uppy,
    fileIdNameMap,
    completedUploads,
    requestWorkerUpdateFileDatabase,
    recordUploadedFileToDB,
    sendPendingFailuresCallback,
  ]);

  const handleUppyRestrictionFailed = useCallback((file: GenericUppyFile | undefined, error: Error) => {
    if (file?.id || file?.name) {
      const id = file.name?.split('.')[0] ?? '';
      const extension = file.name?.split('.').pop() || '';
      const filename = fileIdNameMap.get(id);

      pendingFailuresRef.current.push({
        filename: filename || '',
        extension,
        rejectedReason: UploadFailureReason.UnsupportedFileFormat,
      });
    }

    toast.error(error.message);
  }, []);

  useEffect(() => {
    uppy.off('upload-success', handleUppyUploadSuccess);
    uppy.off('complete', handleUppyUploadComplete);
    uppy.off('restriction-failed', handleUppyRestrictionFailed);

    uppy.on('upload-success', handleUppyUploadSuccess);
    uppy.on('complete', handleUppyUploadComplete);
    uppy.on('restriction-failed', handleUppyRestrictionFailed);
  }, [
    handleUppyUploadComplete,
    handleUppyUploadSuccess,
    requestWorkerUpdateFileDatabase,
    handleUppyRestrictionFailed,
    uppy,
  ]);

  const onUploadUppy = useCallback(
    (files: File[]) => {
      uppy.addFiles(files.map((file) => ({ source: 'local', data: file, name: file.name })));
      uppy.upload();

      setIsUploading(true);

      // Create an id for the current upload
      uploadIdRef.current = uuidv4();
      // Don't set checkingForDuplicates here - wait for upload to complete
      // For regular files: handleUppyUploadComplete will trigger duplicate scan
      // For zips: useEffect monitoring activeJobs will trigger duplicate scan after extraction
    },
    [uppy],
  );

  const totalProgress = useUppyState(uppy, (state) => state.totalProgress);

  // Used to pass up to parent state the fact that an upload is ongoing
  useEffect(() => {
    setFilesProcessing?.(isUploading);
  }, [isUploading]);

  useEffect(() => {
    if (files) {
      setFilesExist?.(files.length > 0);
    }
  }, [files, setFilesExist]);

  useEffect(() => {
    const hasActiveJobs = activeJobs && activeJobs.length > 0;
    const hadActiveJobs = previousActiveJobsRef.current.length > 0;

    // Update zipIsProcessing based on whether we have active jobs
    if (hasActiveJobs) {
      setZipIsProcessing(true);
    } else if (!hasActiveJobs && hadActiveJobs) {
      // Zip job just completed (went from having jobs to no jobs)
      setZipIsProcessing(false);
      refetchFiles();
      // Only start duplicate scan if we have an uploadId
      if (uploadIdRef.current) {
        setCheckingForDuplicates(true);
        setupSSEListener();
      }
    }

    // Store current activeJobs for next render
    previousActiveJobsRef.current = activeJobs || [];
  }, [activeJobs]);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden">
      <div className="flex items-center justify-between">
        <ViewToggle view={view} setView={setView} />
        <ButtonGroup>
          {!isMatterCreator && (
            <Button variant="default" size="sm" asChild>
              <Link to={`/matters/${matterId}/settings/upload-history`} className="flex items-center gap-1.5">
                <Icon className="lucide--history" />
                <span>Upload history</span>
              </Link>
            </Button>
          )}
          <Button variant="default" size="sm" onClick={() => setFileTypesModalOpen(true)}>
            <Icon className="lucide--file-check" />
            <span>Permitted files</span>
          </Button>
        </ButtonGroup>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          className="flex h-full min-h-0 flex-col"
          key={view}
          initial={{ x: view === View.Upload ? 100 : -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: view === View.Upload ? 100 : -100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 40 }}
        >
          {view === View.Upload ? (
            <>
              <FileUploadInput
                isUploading={isUploading}
                onDrop={onUploadUppy}
                totalProgress={totalProgress}
                successfulUploads={completedUploadsCount}
                matterId={matterId}
                caseId={allDocumentsCaseId}
                onApideckSuccess={onApideckSuccess}
                isApideckEnabled={user?.organisation?.permissions.includes(OrgPermission.Apideck)}
                activeJobs={activeJobs}
              />

              <div className="mt-4 rounded-md border border-blue-100 bg-blue-50/60 p-4 py-3 text-sm text-blue-500">
                <div>
                  <div className="flex items-center gap-2">
                    <Icon className="lucide--info" />
                    <p className="text-sm text-blue-800">Uploading a large number of files?</p>
                  </div>
                  <p className="mt-1 text-xs text-blue-700">
                    Uploading too many (&gt;1000) files directly may cause performance issues or browser crashes.
                    Compress files into a ZIP archive first, and upload that for better reliability.
                  </p>
                </div>
              </div>

              <div className="mt-4 flex flex-col text-xs text-gray-500">
                <p>
                  We automatically check for identical files, by scanning the file content, and remove them from the
                  upload.
                </p>
              </div>
            </>
          ) : (
            <FileExplorer
              isLoadingFiles={isLoadingFiles}
              allFiles={files ?? []}
              allDocumentsCaseId={allDocumentsCaseId ?? null}
            />
          )}

          <div className="mt-auto text-gray-700">
            {checkingForDuplicates ? (
              <FileDuplicatesInfo duplicateScanSuccess={duplicateScanSuccess} duplicateNumber={duplicateNumber} />
            ) : (
              <ActiveUploadsProgress activeJobs={activeJobs} zipIsProcessing={zipIsProcessing} />
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      <Dialog open={fileTypesModalOpen} onOpenChange={setFileTypesModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Icon className="lucide--file-check" />
              Permitted file types
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <p className="text-gray-700">Wexler supports the following file types:</p>
            <div className="flex flex-wrap gap-x-4 gap-y-2 rounded-md border bg-gray-50 p-3">
              {allowedFileTypes.map((fileType: string) => (
                <div
                  className="rounded-md border bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700"
                  key={fileType}
                >
                  {fileType}
                </div>
              ))}
            </div>
            <ul className="flex list-inside list-disc flex-col gap-2 py-2 text-sm text-gray-700">
              <li>The maximum file size supported is 2GB</li>
              <li>The maximum number of files supported is 100,000</li>
            </ul>
            <p className="text-xs text-gray-600">
              Reach out to the Wexler team if these limits are not sufficient for your case.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
